import React from "react";
import {
  Box,
  Container,
  Grid,
  Link,
  Typography,
  useTheme,
  IconButton,
  Stack,
  Divider,
  useMediaQuery,
  alpha,
} from "@mui/material";
import {
  Facebook as FacebookIcon,
  Twitter as TwitterIcon,
  Instagram as InstagramIcon,
  LinkedIn as LinkedInIcon,
  GitHub as GitHubIcon,
} from "@mui/icons-material";
import { Link as RouterLink } from "react-router-dom";

const Footer = () => {
  const theme = useTheme();
  const currentYear = new Date().getFullYear();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Box
      component="footer"
      sx={{
        width: "100%",
        py: 6,
        px: { xs: 2, sm: 3 },
        backgroundColor:
          theme.palette.mode === "dark"
            ? alpha(theme.palette.background.paper, 0.5)
            : alpha(theme.palette.background.paper, 0.8),
        backdropFilter: "blur(20px)",
        borderTop: `1px solid ${theme.palette.mode === "dark"
            ? alpha(theme.palette.common.white, 0.1)
            : alpha(theme.palette.common.black, 0.05)
          }`,
        mt: "auto",
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={isMobile ? 4 : 8}>
          <Grid item xs={12} md={4}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 800,
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                mb: 2,
                letterSpacing: "-0.02em",
              }}
            >
              RoomLoop
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph sx={{ lineHeight: 1.8, mb: 3 }}>
              The modern platform for virtual events, micro-meetups, and
              collaborative spaces. Connect, share, and grow together.
            </Typography>
            <Stack direction="row" spacing={1}>
              {[FacebookIcon, TwitterIcon, InstagramIcon, LinkedInIcon, GitHubIcon].map((Icon, index) => (
                <IconButton
                  key={index}
                  size="small"
                  sx={{
                    color: "text.secondary",
                    transition: "all 0.2s",
                    "&:hover": {
                      color: "primary.main",
                      transform: "translateY(-2px)",
                      backgroundColor: alpha(theme.palette.primary.main, 0.1),
                    },
                  }}
                >
                  <Icon fontSize="small" />
                </IconButton>
              ))}
            </Stack>
          </Grid>

          <Grid item xs={6} md={4}>
            <Typography
              variant="subtitle1"
              color="text.primary"
              gutterBottom
              fontWeight={700}
              sx={{ mb: 2 }}
            >
              Platform
            </Typography>
            <Stack spacing={1.5}>
              {[
                { text: "Dashboard", path: "/dashboard" },
                { text: "Explore Rooms", path: "/explore" },
                { text: "Create Room", path: "/create-room" },
                { text: "Profile", path: "/profile" },
              ].map((link) => (
                <Link
                  key={link.text}
                  component={RouterLink}
                  to={link.path}
                  color="text.secondary"
                  underline="none"
                  sx={{
                    transition: "color 0.2s",
                    "&:hover": { color: "primary.main" },
                    fontSize: "0.95rem",
                  }}
                >
                  {link.text}
                </Link>
              ))}
            </Stack>
          </Grid>

          <Grid item xs={6} md={4}>
            <Typography
              variant="subtitle1"
              color="text.primary"
              gutterBottom
              fontWeight={700}
              sx={{ mb: 2 }}
            >
              Support
            </Typography>
            <Stack spacing={1.5}>
              {["Help Center", "Privacy Policy", "Terms of Service", "Contact Us"].map((text) => (
                <Link
                  key={text}
                  href="#"
                  color="text.secondary"
                  underline="none"
                  sx={{
                    transition: "color 0.2s",
                    "&:hover": { color: "primary.main" },
                    fontSize: "0.95rem",
                  }}
                >
                  {text}
                </Link>
              ))}
            </Stack>
          </Grid>
        </Grid>

        <Divider sx={{ my: 4, borderColor: alpha(theme.palette.divider, 0.1) }} />

        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            © {currentYear} RoomLoop. All rights reserved.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Designed with ❤️ for community.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;
